define((require) => {
    "use strict";

    var Backbone      = require("backbone"),
        $             = require("jquery"),
        Class         = require("shared/util/Class"),
        {Logger}      = require("shared/util/Logger"),
        Cookie        = require("editor/util/Cookie"),
        RestApi       = require("shared/services/RestApi"),
        Promise       = require("shared/util/Promise"),
        Storage       = require("shared/services/Storage"),
        LoggedIn      = require("shared/security/LoggedIn"),
        Principal     = require("security/Principal"),
        Configuration = require("shared/services/Configuration"),
        UrlUtil       = require("shared/util/UrlUtil");
    var log = new Logger("Login");

    var PERSISTENT_NAME = "token";

    /**
     * @private @type {String}
     * The token from <tt>LoggedIn.principal</tt>. <p>
     *
     * The token will be set as the "Authorization" header for each issued request. The token will never be null for
     * a logged in principal.
     */
    var _token = null;

    var _principal = null;

    /**
     * Adds the authentication token for the authenticated principal to all API requests. <p>
     *
     * Note: The Auth token has also been added to /modules/Auth/Auth.php because that is the matomo dashboard and we use the auth token to login.
     *
     * In addition, the version is added as the "X-Zmags-Creator-Version" header.
     *
     * @param {Object} options The jQuery Ajax options hash; never null.
     */
    function addApiAuthHeader(options) {
        if (_token && options && (options.url.indexOf(RestApi.API_URL) === 0 || UrlUtil.sameHostname(options.url, Configuration.matomoConfig.dashboardUrl) ||
                UrlUtil.sameHostname(options.url, Configuration.inventoryUrl) || UrlUtil.sameHostname(options.url, Configuration.experienceUrl2PngBaseUrl) ||
                UrlUtil.sameHostname(options.url, Configuration.proxyUrl) || UrlUtil.sameHostname(options.url, Configuration.experienceSharedEntityBaseUrl) ||
                UrlUtil.sameHostname(options.url, Configuration.microserviceCompanyUrl))) {
            if (!options.headers) {
                options.headers = {};
            }
            options.headers.Authorization = "Bearer " + _token;
            // Add version to each API request...
            if (Configuration.version) {
                options.headers["X-Zmags-Creator-Version"] = Configuration.version;
            }
        }
    }

    // Only set up these once...
    Backbone.$.ajaxPrefilter(addApiAuthHeader);
    $.ajaxPrefilter(addApiAuthHeader);

    /**
     * Logs in the given principal. <p>
     *
     * Note that the token for the principal may be null in case of company restrictions!
     *
     * @param {Principal} principal The principal to login; never null.
     */
    function login(principal) {
        // If "principal.persistent" is true, we use the default cookie timeout, otherwise create it as a
        // session cookie only (timeout 0). This way, the cookie can still be used for inter-tab communication,
        // e.g. when a new tab is opened, either by the user or by the editor - no additional login is then
        // required. This will work since this controller will be hit again, then reading the token from the
        // (session) cookie. The cookie will thus in any case be cleared on explicit log out, or on browser timeout...
        //
        // CAVEAT: Chrome may keep the session cookies even after the browser is closed, especially if the
        //         user has set the option "Remember where I left off", or if extensions keep running in the
        //         backend. However, as Bo states: this is the same for anybody using Chrome, not just us,
        //         so we decided to use session cookies after all because of the benefits regarding not
        //         having to login into multiple tabs and because of easy inter-tab communication. An small
        //         optimisation could be to bind a call to "logout" on "onbeforeunload" with an associated
        //         count in the cookie for the current number of opened windows. We could also store a
        //         timeout value along with the token, e.g. token + ":" + timestamp, and manually enforce
        //         a smaller timeout, so session cookies at least would not live "forever"...
        //
        // CAVEAT: we do *not* store the actual token as a cookie, but in session storage, since Creator runs
        //         on both http and https, meaning that all requests sent via http will include cookies and would
        //         thus include the access token in plain text otherwise!

        // To test for same principal we have to test on user id, not on token, since the token value can be changed on
        // upgrade to normal (re-)login. The id is just that: an id. The is no way to fetch anything regarding the token
        // based on it via the backend API, so safe enough to use it here...
        var existing = _principal !== null,
            token = Date.now(),
            same = existing && (principal.get("userId") === _principal.get("userId"));

        // Make sure we force a logout if the principal changes in a way that is not allowed, i.e. if different
        // backing user, or if the user changes company...
        if (existing) {
            if (!same) { // different backing user...
                same = false;
                log.warn("Logging out old principal before new one can log in: ", _principal);
                logout(_principal);
            } else if (principal.get("companyId") !== _principal.get("companyId")) { // same backing user, different company (wtf!)
                same = false;
                log.error("Logging out principal that changed company: ", _principal);
                logout(_principal);
            }
        }

        // Save actual (updated) token here from incoming principal...
        _token = principal.get("token");

        // "principal" will always be a new instance here, but may correspond to the existing logged in principal
        // stored in "_principal", if any. If so, we must update the existing one with properties that potentially
        // could be change, as various views could already keep a reference to it, using it as a model. If different
        // principal, the "update" property is null by default, and we simply set it as the logged in principal...
        if (same) {
            var role = principal.get("role"),
                state = principal.get("state");

            _principal.set({
                displayName: principal.get("displayName"),
                type:        principal.get("type"),
                user:        principal.get("user"), // never null for a valid login!
                role:        role,
                state:       state,
                updated:     _principal.get("role") !== role || _principal.get("state") !== state
            }, {silent: true});

            // Already a mock token, so keep using the same...
            token = _principal.get("token");

        // First time login for "principal"...
        } else {
            _principal = principal;
        }

        // Set mock token on principal, so we do not expose it elsewhere...
        _principal.set("token", token, {silent: true});

        var persistent = _principal.get("persistent");

        //
        // On cookies and security, see fx: http://resources.infosecinstitute.com/securing-cookies-httponly-secure-flags/
        //

        // The actual token may live longer than the session cookie, but will only be used if the cookie is set.
        // We cannot, however, store the actual token in *local* storage if not persistent, since the app can
        // be left at any time, e.g. the user entering a new url in the browser, without us having time to
        // clean up (i.e. do a logout). We have to use session storage otherwise, as DnD (and other stuff) will
        // not work otherwise, since it relies on auto login. Once MOS-1509 is in place, i.e. no http in the editor,
        // we can switch to secure cookies only, not using local/session storage any longer...

        // We assume persistent, i.e. "Keep me signed in", per default...
        var use = Storage.Local,
            clear = Storage.Session;

        if (!persistent) {
            use = Storage.Session;
            clear = Storage.Local;
        }

        var synced = use.update(Storage.Keys.LOGIN, PERSISTENT_NAME, _token);

        // And - one final caveat: Safari incognito mode enforces a quota of zero for local storage, i.e. it cannot
        // be used. We thus have to check when writing to local storage - on false, we have to go "non-persistent"
        // and use session storage instead...
        if (!synced) {
            // Try and switch then, so we use session storage. This is only a guess of what is wrong, of course, but
            // if we do not try this here, we cannot login (and use DnD)...
            if (persistent) {
                log.warn("Principal cannot use 'Keep me signed in': " + principal);
                use = Storage.Session;
                synced = use.update(Storage.Keys.LOGIN, PERSISTENT_NAME, _token);
                if (synced) {
                    clear = Storage.Local;
                    persistent = false;
                    _principal.set("persistent", false, {silent: true});

                // Nothing to do then, at all, so keep state as it was...
                } else {
                    use = Storage.Local;
                }
            }
        }
        clear.unset(Storage.Keys.LOGIN, PERSISTENT_NAME);

        // Mock token stored in cookie, for 60 days which is the current read-only timeout for tokens...
        Cookie.set(PERSISTENT_NAME, token, persistent ? 60 * 24 : 0);

        // This will trigger a change event on LoggedIn that modules can listen for...
        LoggedIn._login(_principal);
    }

    /**
     * Logs out the current principal, if any.
     *
     * @param {Principal} [principal] The principal that just tried to log in, but failed to do so because of
     *                                login restrictions. This is <b>NOT</b> (necessarily) the principal to log out.
     *                                On failure to validated credentials, always null. If supplied, this will trigger
     *                                an "error:login_restricted" event from the <tt>LoggedIn</tt> module.
     */
    function logout(principal) {
        LoggedIn._logout(principal);

        if (!_token) {
            return;
        }
        _principal = null;
        _token = null;

        // Destroy the cookie and local/session storage. This will actually have no effect if the cookie is a session
        // cookie (expect perhaps in Chrome, unfortunately), i.e. the user has opted not to remember the cookie,
        // since that will be deleted by default. But it will delete the token from local storage, preventing
        // login with the removed token in any case...
        Cookie.remove(PERSISTENT_NAME);
        Storage.Local.unset(Storage.Keys.LOGIN, PERSISTENT_NAME);
        Storage.Session.unset(Storage.Keys.LOGIN, PERSISTENT_NAME);
        Storage.Local.remove(Storage.Keys.ONLOAD_ACTION_ENABLED);
        Storage.Local.remove(Storage.Keys.CREATOR_CONNECT);
        Storage.Local.remove(Storage.Keys.CONTENT_CONTROL_BETA);
    }

    /**
     * Checks an valid (200!) authorization response and logs in the authorized principal if available. This is never
     * called on authentication errors like 401, 403, nor on 404. <p>
     *
     * On successful login, the authorized principal with a token is resolved and returned; if not valid, a rejected
     * promise is returned, also with the principal, but without any associated token or user.
     *
     * @param {*}        auth        The authorization response (200) from the API; never null.
     * @param {String}   message     A user friendly message describing the type of login method performed (for
     *                               logging only); never null.
     * @param {Boolean} [persistent] True if "Keep me signed in" is checked in the ui, false otherwise. Will default to
     *                               the existing cookie value if not specified.
     *
     * @return {Principal|Promise} The authorized principal, or a rejected promise with the principal (with no token).
     */
    function checkPrincipal(auth, message, persistent) {
        // Create principal based on the authorization response...
        var principal = new Principal(auth);

        // On login restrictions, we can an empty response on otherwise successful login, not an error, so we have
        // to take this into account on actual non-AD login...
        if (principal.get("token")) {
            // Mark principal as persistent ("Keep me signed in") or not...
            principal.set("persistent", typeof persistent === "undefined" ? !!Cookie.get(PERSISTENT_NAME) : persistent);

            // Generate user-friendly message about the type of login...
            message += ": " + principal.get("type");
            if (!principal.get("user").get("confirmedAt")) {
                message += " (Unconfirmed)";
            }
            log.info(message += " -> <" + principal.get("displayName") + ">", auth.user); // log json for clarity, not 'principal.get("user")'!

            // Do the actual login, i.e. henceforth use the access token for the principal to access the API...
            login(principal);

            return principal;
        }

        log.warn(message + " restricted");

        // Ensure the rejected user is logged out (i.e. if it was a re-login)...
        logout(principal);

        return Promise.rejected(principal);
    }

    /**
     * Performs a login based on the specified Ajax options, and returns a promise that will resolve to the logged
     * in user on success. On error, the promise will be rejected, optionally resolving to a company state.
     *
     * @param {String}   type        A user friendly string describing the type of login method performed (for logging);
     *                               never null.
     * @param {*}        options     The Ajax options for the specific type of login; never null.
     * @param {Boolean} [persistent] True if "Keep me signed in" is checked in the ui, false otherwise. Will default to
     *                               the existing cookie value if not specified.
     * @return {Promise} A promise that will resolve to the authorized user, or on rejection resolves to an optional
     *                   company state; never null.
     */
    function doLogin(type, options, persistent) {
        return $.ajax(options).fail((xhr) => {
            // No logout here, we could be trying to get a normal token from a read-only token...
            log.warn(type + " failed", xhr);
            // Special case: too many login attempts if a short period...
            if (xhr.status === 429) {
                LoggedIn._tooManyAttempts(parseInt(xhr.getResponseHeader("Retry-After"), 10));
            }
        }).then((auth) => {
            return checkPrincipal(auth, type, persistent);
        });
    }

    //
    //

    /**
     * The login service. <p>
     *
     * Once a principal is logged in, it will be available via <tt>principal()</tt>. <p>
     *
     * This class provides the raw login methods and manages the authorization token on API requests. Any logic
     * related to routing, displaying the login form, etc. should be handled elsewhere.
     *
     * @class Login
     * @constructor
     */
    var Login = Class.extend({

        /**
         * @private
         * Tries to login via the specified token.
         *
         * @param {String} type  A user friendly string describing the type of login method performed (for logging);
         *                       never null.
         * @param {String} token The token to use for login; never null.
         * @return {Promise} A promise resolving to the logged in principal, a rejected promise with the token-less
         *                   principal with company restrictions, or undefined on failure to login altogether.
         */
        _loginWithToken: function(type, token) {
            // Ensure the token is (still) valid...
            _token = token; // now set as request header!

            let url = `${Configuration.loginWithTokenBaseUrl}/api/token`;

            return doLogin(type, {
                url: url,
                dataType: "json"
            });
        },

        /**
         * Tries to re-login via the current token, e.g. to upgrade the token from unconfirmed or read-only to
         * normal.
         *
         * @return {Promise} A promise resolving to the new logged in principal, where the "updated"
         *                   property will be set to true if the role and/or company state has changed, or undefined
         *                   on failure to login altogether.
         */
        relogin: function() {
            return this._loginWithToken("Re-login with token", _token);
        },

        /**
         * Attempts a login with a potential persisted user, i.e. previously logged in with "Keep me signed in" checked.
         *
         * @return {Promise} A promise resolving to the logged in principal, a rejected promise with the token-less
         *                   principal with company restrictions, or undefined on failure to login altogether.
         */
        loginPersistedUser: function() {
            // Check if we have a stored token to use for the login: this will be local storage when persistent, i.e.
            // when "Keep me signed in" is checked, otherwise session storage...
            var token = Storage.Local.get(Storage.Keys.LOGIN, PERSISTENT_NAME) ||
                        Storage.Session.get(Storage.Keys.LOGIN, PERSISTENT_NAME);

            // Check if there is a valid corresponding (session) cookie. Note that Chrome ignores session cookies,
            // but the backend will check for the token lifespan in any case...
            if (!(token && Cookie.get(PERSISTENT_NAME))) {
                // Make sure any expired token is deleted for consistency...
                Storage.Local.unset(Storage.Keys.LOGIN, PERSISTENT_NAME);
                Storage.Session.unset(Storage.Keys.LOGIN, PERSISTENT_NAME);
                return Promise.rejected();
            }
            // Ensure the token is still valid...
            return this._loginWithToken("Persisted user login", token);
        },

        /**
         * Attempt a login with a username and password.
         *
         * @param {String}   username    The username.
         * @param {String}   password    The password.
         * @param {Boolean} [persistent] True if "Keep me signed in" is checked in the ui, false otherwise. Will default to
         *                               the existing cookie value if not specified.
         *
         * @return {Promise} A promise resolving to the logged in principal, a rejected promise with the token-less
         *                   principal with company restrictions, or undefined on failure to login altogether.
         */
        loginWithPassword: function(username, password, persistent) {
            let url = `${Configuration.loginWithPasswordBaseUrl}/api/token`;
            return doLogin("Password login for <" + username + ">", {
                url: url,
                type: "POST",
                data: JSON.stringify({
                    username: username,
                    password: password
                }),
                contentType: "application/json; charset=utf-8",
                dataType: "json"
            }, persistent);
        },

        /**
         * Attempt a login with a JWT issued by the ZMags Auth0 tenant for SSO.
         *
         * @param {String}  jsonWebToken The JWT issued by the ZMags Auth0 tenant.
         * @return {Promise} A promise resolving to the logged in principal, a rejected promise with the token-less
         *                   principal with company restrictions, or undefined on failure to login altogether.
         */
        loginWithSSO: function(jsonWebToken) {
            let url = `${Configuration.loginWithSsoBaseUrl}/api/token/auth0`;
            return doLogin("JWT login", {
                url: url,
                type: "POST",
                data: JSON.stringify({
                    password: jsonWebToken
                }),
                contentType: "application/json; charset=utf-8",
                dataType: "json"
            });
        },

        /**
         * Attempts a login for a new user using a token supplied in the link.
         *
         * @param {String}  token The access token for the new user; cannot be null. After successful login, the token
         *                        will be deleted, and a new one granted to the user. Hence, clicking the same link
         *                        will never succeed twice if the first call succeeded.
         *
         * @returns {Promise} A promise for having logged in successfully. Will resolve with the logged in principal.
         */
        loginNewUser: function(token) {
            return this._loginWithToken("New user login", token);
        },

        /**
         * Attempts a login with Active Directory credentials.
         *
         * @param {Backbone.Model} model The Active Directory login model. See ADSignInView.
         * @returns {Promise} A promise for having logged in successfully. Will resolve with the logged in principal.
         */
        loginActiveDirectory: function(model) {
            let url = `${Configuration.loginActiveDirectoryBaseUrl}/api/token/ad`;
            return doLogin("Ad login <" + (model.get("adUsername") || model.get("experienceId") || model.get("publicatorUserId")) + ">", {
                url: url,
                type: "POST",
                data: JSON.stringify(model.toJSON()),
                contentType: "application/json; charset=utf-8"
            });
        },

        /**
         * Changes the password of the user currently associated with the logged in principal. <p>
         *
         * This will cause a re-login (new token).
         *
         * @param {String}    password    The new password.
         * @param {Boolean}  [persistent] True if "Keep me signed in" is checked in the ui, false otherwise. Will default
         *                                to the existing cookie value if not specified.
         * @returns {Promise} A promise for the change of password. Will resolve with the logged in principal once
         *                    the change is complete.
         *
         */
        changePassword: function(password, persistent) {
            let user = LoggedIn.user();
            let userId = user.get("id");
            let url = `${Configuration.changePasswordBaseUrl}/api/${RestApi.Collections.USERS}/${userId}/password`;
            return doLogin("Change password for <" + user.get("username") + ">", {
                url: url,
                type: "PUT",
                data: JSON.stringify({password: password}),
                contentType: "application/json; charset=utf-8"
            }, persistent);
        },

        /**
         * Resets the password of the user with the specified email to the supplied password, invalidating the
         * old authentication token for the user in question on success. <p>
         *
         * This will cause a re-login (new token).
         *
         * @param {String}  resetToken   The reset token to use for the reset.
         * @param {String}  email        The email (username) of the user in question.
         * @param {String}  password     The (new) password.
         * @param {Boolean} [persistent] True if "Keep me signed in" is checked in the ui, false otherwise. Will default
         *                               to the existing cookie value if not specified.
         *
         * @returns {Promise} A promise for the reset of password. Will resolve with the logged in principal once
         *                    the change is complete.
         */
        resetPassword: function(resetToken, email, password, persistent) {
            let url = `${Configuration.resetPasswordBaseUrl}/api/${RestApi.Collections.USERS}/password/${resetToken}`;
            return doLogin("Reset password for <" + email + ">", {
                url: url,
                type: "PUT",
                data: JSON.stringify({
                    email:    email,
                    password: password
                }),
                contentType: "application/json; charset=utf-8"
            }, persistent);
        },

        /**
         * Sends a mail to the specified email if a user with said username exist with information
         * about how to reset the password. <p>
         *
         * This will <b>not</b> reset the password. The password reset is not performed until the
         * user clicks an embedded link in said mail that links back to the editor, where the user
         * can confirm the actual reset. <p>
         *
         * The user will remain on the same page, i.e. no navigation is performed on success.
         *
         * @param   {String}  email The email (username) for the user that has forgotten her password;
         *                          cannot be null.
         * @returns {Promise} Promise that resolves when a mail has been sent to the matching user,
         *                    if any.
         */
        forgotPassword: function(email) {
            let url = `${Configuration.forgotPasswordBaseUrl}/api/${RestApi.Collections.USERS}/password/forgot`;
            return $.ajax({
                url:  url,
                type: "PUT",
                data: JSON.stringify({
                    email:   email,
                    baseUrl: UrlUtil.baseUrl() // PROXY IGNORES THIS AS IT's an attach vector for bad emails!
                }),
                contentType: "application/json; charset=utf-8"
            })
                .fail((xhr) => {
                    log.warn("Forgot password for <" + email + "> failed", xhr);
                });
        },

        /**
         * Logs the current principal, if any, out.
         */
        logout: logout

    });

    // Singleton...
    return new Login();
});



// WEBPACK FOOTER //
// ./src/editor/security/Login.js